/**
 * MobilePresenter — Mobile UI strategy for e-commerce.
 *
 * Renders products via MobileProductCarousel (3D carousel appended to document.body),
 * shows toasts via the carousel's built-in toast, and updates the mobile cart row
 * inside the minimized voice bar.
 *
 * Implements the EcommercePresenter interface.
 */
import { EcommercePresenter } from './EcommercePresenter.js';
import { MobileProductCarousel } from './MobileProductCarousel.js';
import { FlyToCart } from './FlyToCart.js';

export class MobilePresenter extends EcommercePresenter {

  /**
   * @param {Object}   config
   * @param {string}   config.primaryColor  - hex color for the carousel
   * @param {Function} config.onProductAdd  - called when user taps Add on a product
   */
  constructor(config = {}) {
    super();
    this.primaryColor = config.primaryColor || '#3b82f6';
    this.onProductAdd = config.onProductAdd || (() => {});
    this.carousel = null;
    this.flyToCart = new FlyToCart({
      getCartTarget: () => this._getCartTarget(),
      onCartBump: () => this._triggerCartBump(),
      onRipple: () => {},
    });
  }

  _getCartTarget() {
    const container = document.getElementById('mobile-voice-call-bar-container');
    if (!container) return null;
    const icon = container.querySelector('[data-ttp-fly-target]');
    if (icon) {
      const r = icon.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) return icon;
    }
    const fallback = container.querySelector('#ttpFlyToCartTarget');
    if (fallback) {
      const r = fallback.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) return fallback;
    }
    return null;
  }

  _triggerCartBump() {
    // Cart bump scale removed — no zoom on add to cart
  }

  // ═══════════════════════════════════════════
  // EcommercePresenter interface
  // ═══════════════════════════════════════════

  showProducts(products, title, _layout) {
    if (!this.carousel) {
      this.carousel = new MobileProductCarousel({
        primaryColor: this.primaryColor,
        onProductAdd: (cardEl, product) => this._handleProductAddWithFly(cardEl, product),
        onAddDisabled: () => this.flyToCart.isAnimating,
        onClose: () => {},
      });
    }
    this.carousel.show(products, title);
  }

  _handleProductAddWithFly(cardEl, product) {
    if (this.flyToCart.isAnimating) return;
    this.carousel?.container?.classList.add('ttp-products-animating');
    this.flyToCart.triggerFly(cardEl, product, () => {
      this.carousel?.container?.classList.remove('ttp-products-animating');
      this.onProductAdd(product);
    }, { preferClone: false });
  }

  hideProducts() {
    if (this.carousel) {
      this.carousel.hide();
    }
  }

  onProductAdded(product, cartInfo) {
    if (this.carousel && this.carousel.container) {
      this.carousel.showToast(product.name, cartInfo);
    }
  }

  updateCart(_cartInfo) {}

  destroy() {
    if (this.flyToCart) this.flyToCart.destroy();
    if (this.carousel) {
      this.carousel.destroy();
      this.carousel = null;
    }
  }

}
